import { IDToken, SigninWithRedirectOptions } from '@okta/okta-auth-js';
import OktaSignIn, { WidgetOptions } from '@okta/okta-signin-widget';

import { getOktaOAuthRedirectParams } from 'app/api/cookie';
import { Theme } from 'app/components/v2/theme';
import { images } from 'app/constants/images';
import { routes } from 'app/constants/routes';
import { nowInSeconds } from 'app/utils/lib/dates';
import { parseJWT } from 'app/utils/lib/string';

export enum OktaTenant {
  TBKBank = 1,
  TriumphExternal = 2,
}

interface RequiredOktaSignInOptions {
  clientId: string;
  issuer: string;
  oktaTenantId: null | number;
  state: string | undefined;
  useClassicEngine: boolean;
}

type GetOktaSignInOptions = Omit<WidgetOptions, 'redirectUri'>;

interface SignInWithOktaWidgetOptions {
  brandColor: string;
  oktaContainerId: string;
  state: string | undefined;
  theme: Theme;
  username: string;
}

export const exchangeOktaCode = async (
  signInInstance: null | OktaSignIn,
  options: SigninWithRedirectOptions,
): Promise<string | undefined> => {
  if (!signInInstance) {
    return undefined;
  }

  if (options.interactionCode) {
    const tokenParams = await signInInstance.authClient.token.prepareTokenParams(options);
    const tokenResponse = await signInInstance.authClient.token.exchangeCodeForTokens(tokenParams);
    signInInstance.authClient.tokenManager.setTokens(tokenResponse.tokens);

    return validateUnexpiredIdToken(signInInstance, tokenResponse.tokens.idToken);
  }

  void signInInstance.authClient.signInWithRedirect(options);
  const idToken = signInInstance.authClient.tokenManager.getTokensSync().idToken;

  return validateUnexpiredIdToken(signInInstance, idToken);
};

export const getOktaIdToken = (signInInstance: null | OktaSignIn): string | undefined => {
  if (!signInInstance) {
    return undefined;
  }

  const idToken = signInInstance.authClient.tokenManager.getTokensSync().idToken;
  const idTokenString = validateUnexpiredIdToken(signInInstance, idToken);
  signInInstance?.remove();

  return idTokenString;
};

const validateUnexpiredIdToken = (
  signInInstance: null | OktaSignIn,
  idToken: IDToken | undefined,
): string | undefined => {
  if (!signInInstance) {
    return undefined;
  }

  if (!idToken) {
    return undefined;
  }

  const parsedToken = parseJWT(idToken.idToken);

  const tokenExp = parsedToken.exp;

  const isJwtExpired = tokenExp < nowInSeconds();

  const hasTokenExpired = isJwtExpired || signInInstance.authClient.tokenManager.hasExpired(idToken);

  if (hasTokenExpired) {
    return undefined;
  }

  return idToken.idToken;
};

export const getOktaRedirect = (options: RequiredOktaSignInOptions) => {
  const signInInstance = getOktaSignInInstance({
    ...options,
    redirectUri: getRedirectUri(options.oktaTenantId),
  });

  void signInInstance?.authClient.token.getWithRedirect();
};

export const getRedirectUri = (oktaTenantId: null | number) => {
  return oktaTenantId === OktaTenant.TriumphExternal ?
    `https://${window.location.host}/${routes.oktaExternal}/`
    : `https://${window.location.host}/${routes.oktaSso}/`;
};

export const getOktaSignInOptions = (options: GetOktaSignInOptions = {}, oktaTenantId: null | number) => {
  const oktaOAuthRedirectParams = getOktaOAuthRedirectParams();
  const clientId = options.clientId ?? oktaOAuthRedirectParams?.clientId;
  const issuer = options.issuer ?? oktaOAuthRedirectParams?.urls.issuer;
  const state = options.state ?? oktaOAuthRedirectParams?.state;

  return {
    flow: 'login',
    redirectUri: getRedirectUri(oktaTenantId),
    ...options,
    clientId,
    issuer,
    state,
  } as SigninWithRedirectOptions;
};

export const getOktaWidgetSignInOptions = (options: RequiredOktaSignInOptions & SignInWithOktaWidgetOptions) => {
  return {
    clientId: options.clientId,
    colors: {
      brand: options.brandColor,
    },
    issuer: options.issuer,
    logo: options.theme.mode === 'light' ? images.logoNew.full : images.logoNewLight.full,
    oktaTenantId: options.oktaTenantId,
    state: options.state,
    useClassicEngine: options.useClassicEngine,
    username: options.username,
  } as GetOktaSignInOptions & RequiredOktaSignInOptions;
};

export const getOktaWidgetSignInInstance = (options: GetOktaSignInOptions & RequiredOktaSignInOptions) => {
  if (!options.clientId || !options.issuer) {
    return null;
  }

  return new OktaSignIn({
    redirectUri: getRedirectUri(options.oktaTenantId),
    ...options,
  });
};

export const getOktaSignInInstance = (options: SigninWithRedirectOptions) => {
  if (!options.clientId || !options.issuer) {
    return null;
  }

  return new OktaSignIn(options);
};

export const removeOktaTokens = () => {
  const options = getOktaSignInOptions({}, null);
  const signInInstance = getOktaSignInInstance(options);

  signInInstance?.authClient.tokenManager.clear();
};

export const signInWithOktaWidget = async (options: RequiredOktaSignInOptions & SignInWithOktaWidgetOptions) => {
  const widgetOptions = getOktaWidgetSignInOptions(options);
  const oktaSignInInstance = getOktaWidgetSignInInstance(widgetOptions);

  const tokens = await oktaSignInInstance?.showSignInToGetTokens({
    el: `#${options.oktaContainerId}`,
  });

  if (tokens) {
    oktaSignInInstance?.authClient.tokenManager.setTokens(tokens);
  }

  return oktaSignInInstance;
};
